import { FiBook } from 'react-icons/fi';

export const academicVocabularyData = {
  courseId: 'academic-vocabulary',
  courseType: 'reading-course',
  title: 'Academic Vocabulary',
  icon: FiBook,
  overview: {
    impact: 'Understand academic texts faster',
    focus: 'Key academic words and phrases',
    uses: 'Reading comprehension, Writing',
    duration: '25 minutes',
  },
  sections: [
    {
      title: 'Why Academic Vocabulary Matters',
      content: `<p>IELTS Reading passages use academic language. Knowing common academic words helps you:</p>`,
      points: [
        'Read passages faster with better comprehension',
        'Recognize paraphrases in questions',
        'Understand complex arguments and relationships',
        'Improve your Writing vocabulary too',
      ],
    },
    {
      title: 'High-Frequency Academic Words',
      content: `<p>Learn these common academic words:</p>`,
      table: {
        headers: ['Word', 'Meaning', 'Example'],
        rows: [
          ['significant', 'important, meaningful', 'significant impact'],
          ['consequently', 'as a result', 'consequently, changes occurred'],
          ['assume', 'suppose, accept as true', 'we can assume that...'],
          ['evident', 'obvious, clear', 'it is evident that...'],
          ['crucial', 'essential, critical', 'crucial factor'],
          ['moreover', 'in addition', 'moreover, studies show...'],
          ['phenomenon', 'event, occurrence', 'natural phenomenon'],
          ['constitute', 'make up, form', 'women constitute 60%'],
        ],
      },
    },
    {
      title: 'Transition Words',
      content: `<p>These words show relationships between ideas:</p>`,
      table: {
        headers: ['Relationship', 'Words'],
        rows: [
          ['Addition', 'furthermore, moreover, in addition, also'],
          ['Contrast', 'however, nevertheless, despite, although'],
          ['Cause/Effect', 'therefore, consequently, thus, hence'],
          ['Example', 'for instance, for example, such as, namely'],
          ['Conclusion', 'in conclusion, overall, to summarize'],
        ],
      },
    },
    {
      title: 'Dealing with Unknown Words',
      content: `<p>When you encounter an unknown word:</p>`,
      points: [
        'Use context: What\'s the topic? What makes sense here?',
        'Look at word parts: prefixes, roots, suffixes',
        'Check nearby synonyms or explanations',
        'Don\'t stop—keep reading if the word isn\'t essential',
      ],
      examples: [
        { 
          label: 'Context clue', 
          text: '"The ubiquitous smartphone..." — "ubiquitous" is linked to smartphones being everywhere.' 
        },
        { 
          label: 'Word parts', 
          text: '"anti-" = against, so "anti-social" = against social' 
        },
      ],
    },
  ],
  exercises: [
    {
      title: 'Match Academic Words',
      type: 'select',
      instructions: 'Choose the closest meaning for each academic word.',
      questions: [
        {
          text: '"The research had significant implications."',
          options: ['important', 'small', 'unclear'],
          answer: 'important',
          explanation: '"Significant" means important or meaningful.',
        },
        {
          text: '"Consequently, the project was delayed."',
          options: ['as a result', 'despite this', 'surprisingly'],
          answer: 'as a result',
          explanation: '"Consequently" indicates cause and effect.',
        },
        {
          text: '"The phenomenon is well documented."',
          options: ['event/occurrence', 'problem', 'solution'],
          answer: 'event/occurrence',
          explanation: '"Phenomenon" means an observable event.',
        },
        {
          text: '"Moreover, additional research is needed."',
          options: ['in addition', 'however', 'finally'],
          answer: 'in addition',
          explanation: '"Moreover" adds information to a previous point.',
        },
        {
          text: '"The study demonstrated a clear correlation."',
          options: ['showed', 'denied', 'questioned'],
          answer: 'showed',
          explanation: '"Demonstrated" = showed, proved, revealed.',
        },
        {
          text: '"The data indicates a downward trend."',
          options: ['suggests/shows', 'contradicts', 'ignores'],
          answer: 'suggests/shows',
          explanation: '"Indicates" means shows or suggests.',
        },
      ],
    },
    {
      title: 'Identify Relationships',
      type: 'select',
      instructions: 'What relationship does each transition word show?',
      questions: [
        {
          text: '"Nevertheless, the results were positive."',
          options: ['Contrast', 'Cause/Effect', 'Addition'],
          answer: 'Contrast',
          explanation: '"Nevertheless" shows contrast with a previous point.',
        },
        {
          text: '"Hence, we concluded that..."',
          options: ['Cause/Effect', 'Addition', 'Example'],
          answer: 'Cause/Effect',
          explanation: '"Hence" indicates a result or conclusion.',
        },
        {
          text: '"For instance, many cities have..."',
          options: ['Example', 'Contrast', 'Conclusion'],
          answer: 'Example',
          explanation: '"For instance" introduces an example.',
        },
        {
          text: '"Furthermore, the study also found..."',
          options: ['Addition', 'Contrast', 'Cause/Effect'],
          answer: 'Addition',
          explanation: '"Furthermore" adds more information.',
        },
      ],
    },
    {
      title: 'Word Parts',
      type: 'select',
      instructions: 'What does the prefix or suffix mean?',
      questions: [
        {
          text: 'What does "anti-" mean in "anti-social"?',
          options: ['against', 'before', 'after'],
          answer: 'against',
          explanation: '"Anti-" means against or opposed to.',
        },
        {
          text: 'What does "-tion" indicate in "education"?',
          options: ['noun form', 'verb form', 'adjective'],
          answer: 'noun form',
          explanation: '"-tion" typically creates nouns from verbs.',
        },
        {
          text: 'What does "pre-" mean in "predict"?',
          options: ['before', 'after', 'against'],
          answer: 'before',
          explanation: '"Pre-" means before (predict = say before).',
        },
      ],
    },
    {
      title: 'Fill in Academic Words',
      type: 'fill-in',
      instructions: 'Type the academic word that fits the context.',
      questions: [
        {
          text: 'The study had ___ (important) implications for healthcare.',
          answer: 'significant',
          explanation: '"Significant" is more academic than "important."',
        },
        {
          text: 'The data ___ (shows) a clear upward trend.',
          answer: 'indicates',
          explanation: '"Indicates" is more formal than "shows."',
        },
        {
          text: 'Women ___ (make up) 60% of the workforce.',
          answer: 'constitute',
          explanation: '"Constitute" is the academic equivalent.',
        },
      ],
    },
    {
      title: 'Context Clues Practice',
      type: 'writing',
      instructions: 'Guess the meaning from context.',
      questions: [
        {
          text: '"The ubiquitous smartphone has changed how we communicate." What does "ubiquitous" likely mean?',
          example: 'Everywhere, very common, found everywhere',
          tip: 'Smartphones are everywhere—"ubiquitous" = everywhere/universal.',
        },
        {
          text: '"The phenomenon of climate change is unprecedented in human history." What does "unprecedented" mean?',
          example: 'Never happened before, without previous example',
          tip: '"Un-" = not + "preceded" = before. Never before seen.',
        },
      ],
    },
  ],
  tips: [
    'Learn 5-10 new academic words per week',
    'Use the Academic Word List (AWL) as a resource',
    'Practice using academic words in your own writing',
    'Create flashcards with context sentences',
  ],
  dailyPractice: [
    'Read academic articles and note new vocabulary',
    'Make sentences with new academic words',
    'Review transition words in reading passages',
    'Practice guessing meaning from context',
  ],
  backPath: '/exams/english/#reading',
  backLabel: 'Back to Reading',
  aiContext: 'Academic Vocabulary: Essential academic words and transition phrases for IELTS Reading, including strategies for handling unknown vocabulary.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...academicVocabularyData,
      title: academicVocabularyData.title + ' (Foundations)',
      overview: {
        ...academicVocabularyData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: academicVocabularyData.sections.slice(0, Math.max(2, Math.ceil(academicVocabularyData.sections.length / 2))),
      exercises: academicVocabularyData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...academicVocabularyData,
      title: academicVocabularyData.title + ' (Advanced)',
      overview: {
        ...academicVocabularyData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: academicVocabularyData.sections,
      exercises: academicVocabularyData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(academicVocabularyData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return academicVocabularyData;
}
